import { createFileRoute } from '@tanstack/react-router';
import { Button, Card, Group, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/providers/AuthProvider';
import * as devicesApi from '@/features/users/devices';
import dayjs from 'dayjs';

export const Route = createFileRoute('/_app/settings/devices')({ component: DevicesPage });

function DevicesPage() {
  const { decoded, user } = useAuth();
  const userId = decoded?.sub ?? user?.id ?? '';
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['my-devices', userId],
    queryFn: () => devicesApi.fetchMyDevices(userId),
    enabled: Boolean(userId),
  });

  const disconnectOne = useMutation({
    mutationFn: devicesApi.disconnectOneDevice,
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Dispositivo disconnesso' });
      qc.invalidateQueries({ queryKey: ['my-devices', userId] });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore' }),
  });

  const disconnectAll = useMutation({
    mutationFn: devicesApi.disconnectAllDevices,
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Tutti i dispositivi disconnessi' });
      qc.invalidateQueries({ queryKey: ['my-devices', userId] });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore' }),
  });

  const askDisconnectAll = () =>
    modals.openConfirmModal({
      title: 'Disconnetti tutti i dispositivi',
      children: <Text size="sm">Verrai disconnesso da tutti i dispositivi tranne quello corrente. Confermi?</Text>,
      labels: { confirm: 'Disconnetti tutti', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => void disconnectAll.mutate(),
    });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Dispositivi attivi</Title>
        <Button color="red" variant="light" onClick={askDisconnectAll}>
          Disconnetti tutti
        </Button>
      </Group>
      <Card withBorder padding={0} radius="md">
        {isLoading ? (
          <Group p="lg"><Loader size="sm" /></Group>
        ) : (
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Dispositivo</Table.Th>
                <Table.Th>IP</Table.Th>
                <Table.Th>Ultimo accesso</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((d) => (
                <Table.Tr key={d.id}>
                  <Table.Td>{d.userAgent ?? d.fingerprint?.slice(0, 12)}</Table.Td>
                  <Table.Td>{d.ip ?? '—'}</Table.Td>
                  <Table.Td>{d.lastSeenAt ? dayjs(d.lastSeenAt).format('DD/MM/YYYY HH:mm') : '—'}</Table.Td>
                  <Table.Td>
                    <Button size="xs" variant="subtle" color="red" onClick={() => void disconnectOne.mutate(d.id)}>
                      Disconnetti
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!data?.length && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="lg">
                      Nessun dispositivo registrato
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}
